<script lang="ts" setup>
import { ref, computed, getCurrentInstance, inject, onMounted, nextTick } from "vue";
import { DxPopup, DxPosition } from "devextreme-vue/popup";
import { DxLoadIndicator, DxButton } from "devextreme-vue";
import { useStore } from 'vuex'
import traducao from "@/localization/vistra.vistraia.json";
import ServiceVistraAI from "@/funcoes/ServiceVistraAI"
import { VMarkdownView } from 'vue3-markdown'
import TextareaAutocomplete from "@/components/TextareaAutocomplete.vue";
import { useStorage } from '@vueuse/core';

const store = useStore();
const instance = getCurrentInstance()!.appContext.config.globalProperties;
const serviceVistraAI = new ServiceVistraAI();
const { $vistra } = instance;
const _boxesModules = inject("boxesModules");
const prompt = ref("");
const mensagens = ref([]);
const historico = ref([]);
const perguntas = ref([]);
const botaoEnviarMensagem = ref(false)
const mostrarDataGridCampos = ref(false)
const mostrarPerguntas = ref(false)
const dispositivo = computed(() => $vistra.getDispositivo())
const fullScreen = ref(false);
const loadingGerarPerguntas = ref(false);
const key = useStorage('key-popup', '', sessionStorage)

const nomeUsuario = computed(() => store.state.usuario.nome.replace('.', " ").split(" ")[0]);

$vistra.carregaTraducao([traducao]);
type Props = {
  title: string;
  metaData: object;
};

type Emits = {
  (e: "cancel"): void;
  (e: "confirm"): void;
};

const show = ref(false);
const popupIA = ref(null);
const objetoDataGridCampos = ref(null);
const nomeTipoObjeto = ref("");
const metaData = computed(() => {
  return {
    nomePainel: props.metaData.dadosPainel.nome,
    idPainel: props.metaData.dadosPainel.id.toString(),
    nomeFonteDados: _boxesModules?.dadosIA.value.fonteDadosIA.alias,
    nomeObjeto: _boxesModules?.dadosIA.value.boxSelecionado.name,
    tipoObjeto: nomeTipoObjeto.value
  }
})

const onFullscreen = () => {
  if (dispositivo.value != 'desktop') return;
  fullScreen.value = !fullScreen.value;
}

const toggle = () => {
  show.value = !show.value;
  if (show.value) {
    getNomeTipoObjeto();
    setTimeout(() => {
      document.getElementById("textAreaPromptIA")?.focus()
    }, 1000);
  } else {
    resetPopupIA();
  }
};

const props = defineProps<Props>();
defineEmits<Emits>();
defineExpose({
  toggle,
});

const tema = computed(() => store.state.tema == "tema" ? "light" : "dark");
const avatarIconIA = computed(() => tema.value == "dark" ? "#243944" : "#E5E7EB")
const avatarEstrelaIconIA = computed(() => tema.value == "dark" ? "white" : "#4B5563")
const estrelaIconTitle = computed(() => tema.value == "dark" ? "#FD891F" : "#6B7280")
const showCardInfo = computed(() => mensagens.value.length <= 1);

const getNomeTipoObjeto = () => {
  switch (_boxesModules?.dadosIA.value.boxSelecionado.type) {
    case "Chart":
    case "ChartPieDonut":
      nomeTipoObjeto.value = "Gráfico"
      break;

    case 'Table':
    case 'PivotGrid':
      nomeTipoObjeto.value = "Tabela"
      break;

    case 'GaugeCircular':
    case 'GaugeLinear':
    case 'GaugeBar':
    case 'GaugeBullet':
      nomeTipoObjeto.value = "Medidor"
      break;
  }
}

const abrirDataGrid = () => {
  mostrarDataGridCampos.value = !mostrarDataGridCampos.value
}

const createHistorico = () => {
  historico.value = mensagens.value
    .map((item) => (
      {
        fromUser: item.enviado,
        content: item.mensagem
      }
    )).reverse();
}

const enviarPrompt = async () => {
  if (prompt.value.trim() == "" || botaoEnviarMensagem.value) return

  criarObjetoMensagem()
  rolagemScrollContainerMensagem(true)
  botaoEnviarMensagem.value = true;

  let fonteDadosIA = _boxesModules?.dadosIA.value.fonteDadosIA;
  key.value = gerarChaveCache(fonteDadosIA, 'enviarPrompt');
  let fonteDados = fonteDadosIA.dadosFiltrados ?? fonteDadosIA.dados;

  let response = await serviceVistraAI.enviarPrompt(prompt.value.trim(), fonteDados, historico.value, fonteDadosIA.campos, key.value, metaData.value);
  if (!response.success) {
    $vistra.showMessage(response.message, "error");
    botaoEnviarMensagem.value = false;
    return
  }
  botaoEnviarMensagem.value = false;
  criarObjetoMensagem(false, response.data)
  createHistorico();
  rolagemScrollContainerMensagem();
}

const criarObjetoMensagem = (usuario = true, responseServicesIA = null) => {
  const hora = pegarHora();

  if (usuario) {
    mensagens.value.unshift({ enviado: usuario, mensagem: prompt.value, hora: hora })
  } else {
    mensagens.value.unshift({ enviado: usuario, mensagem: responseServicesIA, hora: hora })
  }
}

const rolagemScrollContainerMensagem = (limparPrompt = false, valorNegativo = false) => {
  setTimeout(() => {
    if (limparPrompt) {
      prompt.value = "";
    }
    if (!valorNegativo)
      document.querySelector(".prompt-container").scrollTop = document.querySelector(".prompt-container").scrollHeight;
    else
      document.querySelector(".prompt-container").scrollTop = -document.querySelector(".prompt-container").scrollHeight;
  }, 50)
}

const pegarHora = () => {
  function pad(s: any) {
    return (s < 10) ? '0' + s : s;
  }
  var date = new Date();
  return [date.getHours(), date.getMinutes()].map(pad).join(':');
}

const resetPopupIA = () => {
  mensagens.value = [];
  prompt.value = "";
  historico.value = [];
  nomeTipoObjeto.value = "";
  mostrarDataGridCampos.value = false;
  _boxesModules.dadosIA.value.boxSelecionado = {}
  mostrarPerguntas.value = false;
  key.value = null;
}

const onSelectionChanged = (titulo: string) => {

  if (titulo == "") return;

  const textarea = document.getElementById("textAreaPromptIA");
  const cursorPosicao = textarea.selectionStart;
  const textoAntesCursor = prompt.value.slice(0, cursorPosicao);
  const textoDepoisCursor = prompt.value.slice(cursorPosicao);
  const palavras = textoAntesCursor.split(/\s+/);
  titulo = `"${titulo}"`;
  palavras[palavras.length - 1] = titulo;
  const novoTextoAntesCursor = palavras.join(' ') + ' ';
  mostrarDataGridCampos.value = false;
  prompt.value = novoTextoAntesCursor + textoDepoisCursor;
  nextTick(() => {
    textarea.focus();
    textarea.setSelectionRange(novoTextoAntesCursor.length, novoTextoAntesCursor.length);
  });
  objetoDataGridCampos.value?.$_instance.deselectAll();
  objetoDataGridCampos.value?.$_instance.clearSelection();
};

const mensagemInicial = () => {
  const hora = pegarHora();
  if (mensagens.value.length == 0) {
    mensagens.value.push(
      {
        enviado: false,
        mensagem: `Olá ${nomeUsuario.value}, como posso ajudar?`,
        hora: hora
      });
  }
}

const EnviarPergunta = (pergunta: string) => {
  prompt.value = pergunta;
  enviarPrompt();
}

const gerarPerguntas = async () => {
  loadingGerarPerguntas.value = true;
  let fonteDadosIA = _boxesModules?.dadosIA.value.fonteDadosIA;
  key.value = gerarChaveCache(fonteDadosIA, 'gerarPerguntas');
  let fonteDados = fonteDadosIA.dadosFiltrados ?? fonteDadosIA.dados;

  let res = await serviceVistraAI.gerarPerguntas([fonteDados[0]], fonteDadosIA.campos, key.value, metaData.value);

  if (!res.success) {
    $vistra.showMessage(res.message, "error");
    loadingGerarPerguntas.value = false;
    return;
  }

  perguntas.value = res.data;
  mostrarPerguntas.value = true
  rolagemScrollContainerMensagem(false, true);
  loadingGerarPerguntas.value = false;
}

const gerarChaveCache = (fonteDados: any, acao: string) => {
  return `${acao}-${fonteDados.idPainel}-${fonteDados.id}-${store.getters.usuario.id}-${fonteDados.ultimaAtualizacao}`;
}

onMounted(() => {
  document.addEventListener("click", (event: any) => {
    if (!!document.getElementById("btnAbrirDataGrid") && !document.getElementById("btnAbrirDataGrid")?.contains(event.target)) {
      mostrarDataGridCampos.value = false;
    }
  })
})
</script>

<template>
  <DxPopup width="25%" height="90%" container=".dx-viewport" title-template="title" :enable-body-scroll="false"
    @hiding="resetPopupIA" @showing="mensagemInicial" content-template="popup-content" ref="popupIA"
    :drag-enabled="true" v-model:visible="show" :wrapper-attr="{ class: 'ia-popup' }" :hide-on-outside-click="true"
    :resize-enabled="true" :full-screen="dispositivo == 'desktop' ? fullScreen : true" min-width="25%" min-height="50%">
    <DxPosition v-if="dispositivo == 'desktop'" at="bottom right" of="window" collision="fit" boundary-offset="20 20" />

    <template #title>
      <div class="dark:bg-[#243944] bg-white px-2 h-[60px] flex items-center row">

        <div class="flex row col-4"></div>

        <div class="flex justify-center col items-center">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M13.3278 0.737576C13.304 0.554109 13.0375 0.554109 13.0143 0.737576C12.8321 2.16603 11.7075 3.28994 10.2797 3.4722C10.0956 3.49602 10.0956 3.76252 10.2797 3.78569C11.7075 3.96788 12.832 5.09248 13.0143 6.52032C13.0375 6.70443 13.304 6.70443 13.3278 6.52032C13.51 5.09251 14.6339 3.96796 16.0624 3.78569C16.2465 3.76252 16.2465 3.49602 16.0624 3.4722C14.634 3.29002 13.5101 2.16606 13.3278 0.737576Z"
              :fill="estrelaIconTitle" />
            <path
              d="M6.75844 4.29674C6.71595 3.96522 6.23636 3.96522 6.19388 4.29674C5.81086 7.29652 3.44903 9.65904 0.448591 10.0414C0.117071 10.0839 0.117071 10.5641 0.448591 10.6059C3.44903 10.9889 5.81089 13.3514 6.19388 16.3512C6.23637 16.6827 6.71595 16.6827 6.75844 16.3512C7.14081 13.3514 9.50328 10.9889 12.5037 10.6059C12.8346 10.5641 12.8346 10.0838 12.5037 10.0414C9.50328 9.65899 7.14077 7.29652 6.75844 4.29674Z"
              :fill="estrelaIconTitle" />
          </svg>
          <p class="pl-1 text-[15px] dark:text-white text-[#4B5563]">{{ title }}</p>
        </div>

        <div class="flex row col-4 justify-end items-center mr-2">
          <button type="button" v-if="dispositivo == 'desktop'" @click="onFullscreen"
            class="mr-1 bg-transparent w-[1rem]">
            <i v-if="!fullScreen" role="presentation"
              class="fa-light fa-expand cursor-pointer text-md dark:text-white text-[#4B5563] transition-all"></i>
            <i v-else role="presentation"
              class="fa-light fa-compress cursor-pointer text-md dark:text-white text-[#4B5563] transition-all"></i>
          </button>
          <button type="button" @click="toggle" class="bg-transparent w-[1rem]">
            <i role="presentation"
              class="fa-light fa-xmark cursor-pointer text-md dark:text-white text-[#4B5563] transition-all"></i>
          </button>
        </div>
      </div>
    </template>

    <template #popup-content>
      <div class="card-info absolute w-full top-[70px] flex justify-center"
        v-if="showCardInfo && _boxesModules.dadosIA.value.boxSelecionado.name != null">
        <div class="card-info-content p-2  w-[90%] h-full">
          <p class="dark:text-[#9CA3AF]">{{ nomeTipoObjeto }}: {{ _boxesModules.dadosIA.value.boxSelecionado.name }}</p>
        </div>
      </div>
      <div class="prompt-container px-3 pb-4">
        <div class="flex" :class="[item.enviado ? 'justify-end' : '']" v-for="item in mensagens" :key="item.mensagem">
          <div class="mb-3 max-w-[80%] justify-self-end" v-if="item.enviado">
            <br>
            <div class="w-auto">
              <div class="box-mensagem-enviado flex justify-end w-full"> {{ item.mensagem }} </div>
              <div class="flex justify-end mt-1">{{ item.hora }}</div>
            </div>
          </div>
          <div class="mb-3 row" v-else>
            <div class="ia-avatar w-[40px]">
              <svg width="33" height="33" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="0.5" y="0.360016" width="32" height="32" rx="16" :fill="avatarIconIA" />
                <g clip-path="url(#clip0_6004_1827)">
                  <path
                    d="M21.6277 8.49759C21.6039 8.31412 21.3374 8.31412 21.3142 8.49759C21.1321 9.92604 20.0075 11.0499 18.5796 11.2322C18.3955 11.256 18.3955 11.5225 18.5796 11.5457C20.0074 11.7279 21.132 12.8525 21.3142 14.2803C21.3374 14.4644 21.6039 14.4644 21.6277 14.2803C21.8099 12.8525 22.9339 11.728 24.3624 11.5457C24.5465 11.5225 24.5465 11.256 24.3624 11.2322C22.9339 11.05 21.81 9.92607 21.6277 8.49759Z"
                    :fill="avatarEstrelaIconIA" />
                  <path
                    d="M15.0585 12.0568C15.016 11.7253 14.5364 11.7253 14.4939 12.0568C14.1109 15.0566 11.7491 17.4191 8.74864 17.8014C8.41712 17.8439 8.41712 18.3242 8.74864 18.366C11.7491 18.749 14.1109 21.1115 14.4939 24.1113C14.5364 24.4428 15.016 24.4428 15.0585 24.1113C15.4409 21.1115 17.8033 18.749 20.8038 18.366C21.1347 18.3242 21.1347 17.8439 20.8038 17.8014C17.8033 17.4191 15.4408 15.0566 15.0585 12.0568Z"
                    :fill="avatarEstrelaIconIA" />
                </g>
                <defs>
                  <clipPath id="clip0_6004_1827">
                    <rect width="16" height="16" fill="white" transform="translate(8.5 8.36002)" />
                  </clipPath>
                </defs>
              </svg>
            </div>
            <div class="w-[80%]">
              <label class="text-sm"><b>Vistra IA</b></label>
              <br>
              <div class="box-mensagem-recebido w-full">
                <v-markdown-view mode="black" :content="item.mensagem"></v-markdown-view>
              </div>
              <div class="mt-1">{{ item.hora }}</div>
            </div>
          </div>
        </div>
        <div class="gerar-perguntas">
          <div class="flex w-full row justify-center">
            <div class="w-full dark:text-[#9CA3AF] text-center" v-if="!mostrarPerguntas">
              <label>Descubra insights instantâneos sobre seus dados gerando perguntas predefinidas com um
                clique.</label>
            </div>
            <div class="btn-perguntas" v-if="!mostrarPerguntas" @click="gerarPerguntas">
              <div class="flex w-full">
                <div class="w-full justify-center text-center"> Gerar Perguntas</div>
                <div class="justify-end" v-if="loadingGerarPerguntas">
                  <DxLoadIndicator :height="15" :width="15" />
                </div>
              </div>
            </div>
            <div class="w-full dark:text-[#9CA3AF] text-center" v-if="mostrarPerguntas">
              <label>Aqui estão algumas das perguntas <br> que você pode fazer ao Vistra AI</label>
            </div>

            <div class="btn-perguntas" @click="EnviarPergunta(pergunta)" v-for="(pergunta, index) in perguntas"
              :key="index" v-if="mostrarPerguntas">
              {{ pergunta }}
            </div>
            <div class="btn-perguntas" v-if="mostrarPerguntas" @click="gerarPerguntas">
              <div class="flex w-full">
                <div class="w-full justify-start">Gerar novas perguntas</div>
                <div class="justify-end" v-if="loadingGerarPerguntas">
                  <DxLoadIndicator :height="15" :width="15" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="prompt-textbox flex items-center !w-full p-3">
        <TextareaAutocomplete v-model="prompt" maxHeight="150px" width="85%"
          :dados="$vistra.ordenarPor(_boxesModules?.dadosIA.value.fonteDadosIA.campos, 'titulo')"
          @onEnter="enviarPrompt">

          <div class="datagrid-campos w-[80%]" v-if="mostrarDataGridCampos" id="modalDataGrid">
            <div class="header-container w-full flex justify-center">
              <div
                class="w-[92%] pt-3 pb-1 flex justify-between items-center border-b border-solid border-[#9CA3AF] text-bold">
                <p>Campos de Fontes de dados</p>
                <i role="presentation"
                  class="mr-2 fa-light fa-circle-info cursor-pointer text-md dark:text-[#9CA3AF] text-[#4B5563] transition-all"></i>
              </div>
            </div>
            <ul class="lista-campos" id="objetoDataGridCampos" ref="objetoDataGridCampos">
              <div>
                <li
                  v-for="(option, index) in $vistra.ordenarPor(_boxesModules?.dadosIA.value.fonteDadosIA.campos, 'titulo')"
                  :key="index" class="lista-campos-item mx-3" @click="onSelectionChanged(option.titulo)">
                  {{ option.titulo }}
                </li>
              </div>
            </ul>
          </div>

          <template v-slot:after>
            <button type="button" @click="abrirDataGrid" id="btnAbrirDataGrid"
              class="fields-button bg-transparent text-center" v-if="!botaoEnviarMensagem">
              <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M15.3333 9.05505H19.5C19.721 9.05505 19.9329 9.14285 20.0892 9.29913C20.2455 9.45541 20.3333 9.66737 20.3333 9.88839V15.7217C20.3333 15.9427 20.2455 16.1547 20.0892 16.311C19.9329 16.4673 19.721 16.5551 19.5 16.5551H15.3333M9.08329 9.05505H4.49996C4.27895 9.05505 4.06698 9.14285 3.9107 9.29913C3.75442 9.45541 3.66663 9.66737 3.66663 9.88839V15.7217C3.66663 15.9427 3.75442 16.1547 3.9107 16.311C4.06698 16.4673 4.27895 16.5551 4.49996 16.5551H9.08329M12 5.30505V20.3051M6.99996 12.8051H8.66663M15.3333 12.8051H17"
                  :stroke="avatarEstrelaIconIA" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
            <div>
              <DxButton class="send-button bg-transparent" icon="fa-light fa-paper-plane-top text-primary-500"
                v-if="!botaoEnviarMensagem" @click="enviarPrompt" />
              <DxLoadIndicator v-else class="send-button mb-2" id="small-indicator" :height="20" :width="20" />
            </div>

          </template>
        </TextareaAutocomplete>
      </div>
    </template>

  </DxPopup>
</template>

<style lang="scss">
.ia-popup {

  overflow: hidden;

  .btn-perguntas {
    @apply w-[80%] mt-3 p-3 rounded cursor-pointer dark:text-white text-[#4B5563];
    @apply border border-solid dark:border-[#4B5563] border-[#9CA3AF] hover:border-[#ced1d7] dark:hover:border-white;
  }

  .lista-campos {
    @apply dark:text-white text-[#4B5563] bg-[#E5E7EB] dark:bg-[#243944];
    border: none;
    border-radius: 0 0 10px 10px;
  }

  .lista-campos-item {
    padding: 10px 25px;
    font-size: 14px;
  }

  .lista-campos li:hover,
  .lista-campos-active {
    @apply text-black bg-[#F2F2F2] dark:bg-[#16252E] dark:text-white;
    border-radius: 6px;

  }

  .dx-overlay-content {
    border-radius: 12px !important;
  }

  .dx-popup-content {
    @apply p-0 border-none #{!important};
    overflow-y: hidden !important;
  }

  .gerar-perguntas {
    @apply flex w-full justify-center mt-[25%] mb-[15%];
  }

  .card-info {
    .card-info-content {
      border-radius: 4px;
      @apply dark:bg-[#243944] bg-white border-solid border dark:border-[#0E1F29] border-[#E5E7EB];
    }
  }


  .prompt-textbox {
    @apply border-solid border-t dark:border-[#4B5563] border-[#D1D5DB] dark:bg-[#1A2B36] bg-[#F3F4F6];
    max-height: 180px;
    height: auto;
    min-height: 60px;
    position: absolute;
    bottom: 0;
  }

  .prompt-container {
    overflow-y: auto;
    height: 90%;
    display: flex;
    flex-direction: column-reverse;
    @apply bg-[#F2F2F2] dark:bg-[#16252E];

    .box-mensagem-enviado {
      background: #6FAD28;
      color: white;
      padding: 8px 12px 8px 12px;
      border-radius: 8px 0px 8px 8px;
    }

    .box-mensagem-recebido {
      @apply bg-[#E5E7EB] dark:bg-[#243944];
      padding: 8px 12px 8px 12px;
      border-radius: 0px 8px 8px 8px;
    }
  }

  .header-container {
    @apply bg-[#E5E7EB] dark:bg-[#243944];
  }

  .datagrid-campos {
    position: absolute !important;
    bottom: 80px;
    right: 0px;
    z-index: 10;
    width: 500px;
    max-height: 300px;
      /* Para navegadores WebKit (Chrome, Safari, Edge) */
      ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
      }

      ::-webkit-scrollbar-thumb {
          background-color: #6FAD28 !important;
          border-radius: 10px;
      }

      /* Para Firefox */
      html {
          scrollbar-width: thin;
          background-color: #6FAD28 !important;
      }

      /* Para navegadores antigos (IE/Edge legacy) */
      body {
          overflow-y: scroll; /* Isso é apenas um fallback */
      }
  }

  #objetoDataGridCampos {
    max-height: 200px !important;
    overflow-y: auto;
    z-index: 50;

    .dx-scrollable-scroll-content {
      background-color: #6FAD28 !important;
    }
  }

  .send-button {
    position: absolute !important;
    bottom: 13px;
    right: 10px;
  }

  .fields-button {
    position: absolute !important;
    bottom: 13px;
    right: 45px;
  }
}
</style>
